import _extends from "@babel/runtime/helpers/extends";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

var _propTypes, _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5;

import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Fonts } from '@vds-core/typography';
import { withVDSManager, calculateRem, checkIfMobileDevice } from '@vds-core/utilities';
import { AccessibilityTokens } from '@vds-tokens/accessibility';
import { ColorTokens } from '@vds-tokens/color';
import { LayoutTokens } from '@vds-tokens/layout';
import { TypographyTokens } from '@vds-tokens/typography';

function _calculateButtonHeight() {
  return 'auto';
}

function _calculateMinWidth(size) {
  if (size === 'large') {
    return calculateRem(76);
  }

  return undefined;
}

function _calculateBorderRadius(size) {
  var borderRadius;

  if (size === 'small') {
    borderRadius = 32;
  } else {
    borderRadius = 44;
  }

  return calculateRem(borderRadius);
}

function _calculateSideMargin(size, width) {
  var sideMargin, verticalMargin;

  if (size === 'small') {
    sideMargin = width === 'autoTight' ? LayoutTokens.space['3X'].value : LayoutTokens.space['4X'].value;
    verticalMargin = LayoutTokens.space['2X'].value;
  } else {
    sideMargin = width === 'autoTight' ? LayoutTokens.space['5X'].value : LayoutTokens.space['6X'].value;
    verticalMargin = LayoutTokens.space['3X'].value;
  }

  return calculateRem(verticalMargin, sideMargin);
}

var propTypes = (_propTypes = {
  /**
   * @ignore
   */
  use: PropTypes.oneOf(['primary', 'secondary']),

  /**
   * Accepts a string value to render as the text inside button.
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string, PropTypes.array]).isRequired,

  /**
   *  Button can no longer receive tab navigation focus if -1 is passed.
   */
  tabIndex: PropTypes.number,

  /**
   * If true, button will render as disabled.
   */
  disabled: PropTypes.bool,

  /**
   * Callback function executed when button is clicked.
   */
  onClick: PropTypes.func,

  /**
   * Accepts small or large and renders the associated button size.
   */
  size: PropTypes.oneOf(['small', 'large'])
}, _defineProperty(_propTypes, "size", PropTypes.oneOf(['small', 'large'])), _defineProperty(_propTypes, "width", PropTypes.oneOfType([PropTypes.number, PropTypes.string])), _defineProperty(_propTypes, "display", PropTypes.oneOf(['flex', 'inline-block', 'block'])), _defineProperty(_propTypes, "ariaLabel", PropTypes.string), _defineProperty(_propTypes, "calculateButtonHeight", PropTypes.func), _defineProperty(_propTypes, "calculateBorderRadius", PropTypes.func), _defineProperty(_propTypes, "calculateSideMargin", PropTypes.func), _defineProperty(_propTypes, "smallButtonPadding", PropTypes.bool), _defineProperty(_propTypes, "surface", PropTypes.oneOf(['light', 'dark'])), _defineProperty(_propTypes, 'data-analyticstrack', PropTypes.string), _defineProperty(_propTypes, 'data-track', PropTypes.string), _defineProperty(_propTypes, 'data-track-ignore', PropTypes.string), _defineProperty(_propTypes, 'data-clickstream', PropTypes.string), _defineProperty(_propTypes, "dataLoc", PropTypes.string), _defineProperty(_propTypes, 'data-sitecat-level', PropTypes.string), _defineProperty(_propTypes, 'data-sitecat-position', PropTypes.string), _defineProperty(_propTypes, 'data-sitecat-datatrack', PropTypes.string), _defineProperty(_propTypes, 'data-sitecat-cta', PropTypes.string), _defineProperty(_propTypes, "href", PropTypes.string), _propTypes);
var defaultProps = {
  use: 'primary',
  children: null,
  disabled: false,
  size: 'large',
  display: 'flex',
  width: 'auto',
  surface: 'light',
  onClick: undefined,
  calculateButtonHeight: _calculateButtonHeight,
  calculateMinWidth: _calculateMinWidth,
  calculateBorderRadius: _calculateBorderRadius,
  calculateSideMargin: _calculateSideMargin,
  smallButtonPadding: true
};
/**
 * @component
 * */

var HitArea = /*#__PURE__*/styled.span.withConfig({
  displayName: "HitArea",
  componentId: "VDS__sc-bc3yhn-0"
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  height: ", ";\n  width: 100%;\n  left: 50%;\n  position: absolute;\n  transform: translate(-50%, -50%);\n  text-align: center;\n  top: 50%;\n  content: '';\n  display: inline-block;\n  pointer-events: none; /* pointer-events is added here to ensure the correct item is returned by the onclick handler */\n  &:hover {\n    cursor: pointer;\n  }\n"])), calculateRem(44));
/* 
This must be display: inline-block to allow for overflow ellipsis to show. 
Padding top/bottom is necessary for text to be centered within button. 
StyledChildWrapper needs to have 100% height because it needs to be the click event target element
*/

var StyledChildWrapper = /*#__PURE__*/styled.span.withConfig({
  displayName: "StyledChildWrapper",
  componentId: "VDS__sc-bc3yhn-1"
})(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  position: relative;\n  max-width: 100%;\n  box-sizing: border-box;\n  display: inline-block;\n  vertical-align: middle;\n  align-items: center;\n  background: transparent;\n  justify-content: center;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  text-align: center;\n  -webkit-tap-highlight-color: transparent;\n  pointer-events: none; /* pointer-events is added here to ensure the correct item is returned by the onclick handler */\n  &:focus {\n    outline: none;\n  }\n  ", ";\n  &:hover {\n    outline: none;\n    border: none;\n  }\n"])), function (_ref) {
  var size = _ref.size,
      width = _ref.width,
      calculateSideMargin = _ref.calculateSideMargin;

  switch (size) {
    case 'small':
      return "\n          padding: ".concat(calculateSideMargin(size, width), ";\n        ");

    case 'block':
      return "\n          display: block;\n          padding: ".concat(calculateSideMargin(size, width), ";\n        ");

    default:
      return "\n          padding: ".concat(calculateSideMargin(size, width), ";\n        ");
  }
});

var findWidth = function findWidth(width) {
  if (typeof width === 'string') return width;
  return calculateRem(width);
};

var buttonStyles = /*#__PURE__*/css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  padding: 0;\n  margin: 0;\n  border-width: ", ";\n  border-radius: ", ";\n  box-sizing: border-box;\n  cursor: pointer;\n  display: ", ";\n  flex-direction: row;\n  align-items: center;\n  justify-content: center;\n  font-size: ", ";\n  line-height: ", ";\n  font-family: ", ";\n  font-weight: ", ";\n  height: ", ";\n  position: relative;\n  text-align: center;\n  text-decoration: none;\n  touch-action: ", ";\n  pointer-events: ", ";\n  vertical-align: middle;\n  width: ", ";\n  min-width: ", ";\n  outline: none;\n  -webkit-tap-highlight-color: transparent;\n  ", ";\n  background-color: ", ";\n  border: ", " solid ", ";\n  color: ", ";\n  ", ";\n  ", ";\n\n  ", ";\n\n  ", ";\n"])), calculateRem(1), function (_ref2) {
  var calculateBorderRadius = _ref2.calculateBorderRadius,
      size = _ref2.size;
  return calculateBorderRadius(size);
}, function (_ref3) {
  var display = _ref3.display;
  return display;
}, calculateRem(TypographyTokens.fontsize.body[16].value), calculateRem(TypographyTokens.lineheight.body[20].value), Fonts.VerizonNHGeDS, TypographyTokens.fontweight.bold.value, function (_ref4) {
  var calculateButtonHeight = _ref4.calculateButtonHeight,
      size = _ref4.size;
  return calculateButtonHeight(size);
}, function (_ref5) {
  var disabled = _ref5.disabled;
  return disabled ? 'none' : 'manipulation';
}, function (_ref6) {
  var disabled = _ref6.disabled;
  return disabled && 'none';
}, function (_ref7) {
  var width = _ref7.width;
  return findWidth(width);
}, function (_ref8) {
  var calculateMinWidth = _ref8.calculateMinWidth,
      size = _ref8.size;
  return calculateMinWidth(size);
}, function (_ref9) {
  var size = _ref9.size;

  if (size === 'block') {
    return "\n        display: block;\n        width: 100%;\n      ";
  } else if (size === 'small') {
    return "\n        font-size: ".concat(calculateRem(TypographyTokens.fontsize.body[12].value), ";\n        line-height: ").concat(calculateRem(TypographyTokens.lineheight.body[16].value), ";\n        font-weight: ").concat(TypographyTokens.fontweight.bold.value, ";\n        white-space: nowrap;\n        font-family: ").concat(Fonts.VerizonNHGeTX, ";\n      ");
  } else {
    return "\n        white-space: nowrap;\n        font-weight: ".concat(TypographyTokens.fontweight.bold.value, ";\n        letter-spacing: ").concat(calculateRem(TypographyTokens.letterspacing.wide.value), ";\n      ");
  }
}, function (_ref10) {
  var secondary = _ref10.secondary;
  return secondary ? 'transparent' : ColorTokens.background.primary.dark.value;
}, calculateRem(1), ColorTokens.elements.primary.onlight.value, function (_ref11) {
  var secondary = _ref11.secondary;
  return secondary ? ColorTokens.elements.primary.onlight.value : ColorTokens.elements.primary.ondark.value;
}, function (_ref12) {
  var disabled = _ref12.disabled,
      surface = _ref12.surface,
      secondary = _ref12.secondary,
      calculateBorderRadius = _ref12.calculateBorderRadius,
      size = _ref12.size,
      isMobileDevice = _ref12.isMobileDevice;
  return !disabled && "\n     &:focus:not(:hover) {\n      outline: none;\n      &::before {\n        border-color: ".concat(surface === 'dark' ? AccessibilityTokens.color.focusring.ondark.value : AccessibilityTokens.color.focusring.onlight.value, ";\n        border: ").concat(calculateRem(AccessibilityTokens.width.focusring.value), " ").concat(AccessibilityTokens.borderstyle.focusring.value, " ").concat(surface === 'dark' ? AccessibilityTokens.color.focusring.ondark.value : AccessibilityTokens.color.focusring.onlight.value, ";\n        border-radius: ").concat(calculateBorderRadius(size), ";\n        content: '';\n        height: calc(100% + ").concat(calculateRem(6), ");\n        left: 50%;\n        position: absolute;\n        top: 50%;\n        transform: translate(-50%, -50%);\n        width: calc(100% + ").concat(calculateRem(6), ");     \n      }\n    }\n      &:hover:not(:active) {\n        outline: none;\n        ").concat(!isMobileDevice && "\n            box-shadow: 0 0 0 ".concat(calculateRem(1), " ").concat(surface === 'dark' ? ColorTokens.elements.primary.ondark.value : ColorTokens.elements.primary.onlight.value, ";\n            transition: ease-out 0.1s;\n        "), ";\n      }\n      &:hover:not(:active) span {\n        border: none;\n      }\n      &:active {\n        color: ").concat(surface === 'dark' ? secondary ? ColorTokens.interactive.active.ondark.value : ColorTokens.elements.primary.onlight.value : secondary ? ColorTokens.interactive.active.onlight.value : ColorTokens.elements.primary.ondark.value, ";\n        border-color: ").concat(surface === 'dark' ? ColorTokens.interactive.active.ondark.value : ColorTokens.interactive.active.onlight.value, ";\n        ").concat(!isMobileDevice && "\n          box-shadow: 0 0 0 ".concat(calculateRem(1), " ").concat(surface === 'dark' ? ColorTokens.interactive.active.ondark.value : ColorTokens.interactive.active.onlight.value, ";\n        "), ";\n        background-color: ").concat(surface === 'dark' && !secondary ? ColorTokens.interactive.active.ondark.value : surface !== 'dark' && !secondary ? ColorTokens.interactive.active.onlight.value : 'transparent', ";\n      }\n      &:focus:active:not(:hover) {\n        box-shadow: none;\n      }\n    ");
}, function (_ref13) {
  var surface = _ref13.surface,
      disabled = _ref13.disabled,
      secondary = _ref13.secondary;
  return surface === 'dark' && !disabled && "\n    background-color: ".concat(secondary ? 'transparent' : ColorTokens.background.primary.light.value, ";\n    border: ").concat(calculateRem(1), " solid ").concat(ColorTokens.elements.primary.ondark.value, ";\n    color: ").concat(secondary ? ColorTokens.elements.primary.ondark.value : ColorTokens.elements.primary.onlight.value, ";\n\n  ");
}, function (_ref14) {
  var disabled = _ref14.disabled,
      secondary = _ref14.secondary;
  return disabled && "\n    background-color: ".concat(secondary ? 'transparent' : ColorTokens.interactive.disabled.onlight.value, ";\n    border-color: ").concat(ColorTokens.interactive.disabled.onlight.value, ";\n    color: ").concat(secondary ? ColorTokens.interactive.disabled.onlight.value : ColorTokens.elements.primary.ondark.value, ";\n    cursor: default;\n  ");
}, function (_ref15) {
  var disabled = _ref15.disabled,
      surface = _ref15.surface,
      secondary = _ref15.secondary;
  return disabled && surface === 'dark' && "\n    background-color: ".concat(secondary ? 'transparent' : ColorTokens.interactive.disabled.ondark.value, ";\n    border-color: ").concat(ColorTokens.interactive.disabled.ondark.value, ";\n    color: ").concat(secondary ? ColorTokens.interactive.disabled.ondark.value : ColorTokens.elements.primary.onlight.value, "\n  ");
});
var StyledButton = /*#__PURE__*/styled.button.withConfig({
  displayName: "StyledButton",
  componentId: "VDS__sc-bc3yhn-2"
})(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  ", ";\n"])), buttonStyles);
var StyledAnchor = /*#__PURE__*/styled.a.withConfig({
  displayName: "StyledAnchor",
  componentId: "VDS__sc-bc3yhn-3"
})(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n  ", ";\n  width: ", ";\n  justify-content: center;\n"])), buttonStyles, function (_ref16) {
  var width = _ref16.width,
      size = _ref16.size;
  return width !== 'auto' && width !== 'autoTight' && size === 'large' ? findWidth(width) : 'fit-content';
});

var Button = function Button(props) {
  var children = props.children,
      size = props.size,
      width = props.width,
      disabled = props.disabled,
      tabIndex = props.tabIndex,
      ariaLabel = props.ariaLabel,
      display = props.display,
      calculateButtonHeight = props.calculateButtonHeight,
      calculateBorderRadius = props.calculateBorderRadius,
      calculateSideMargin = props.calculateSideMargin,
      smallButtonPadding = props.smallButtonPadding,
      dataLoc = props.dataLoc,
      use = props.use,
      href = props.href;
  var newAriaLabel = ariaLabel ? ariaLabel : typeof children === 'string' ? children : 'Button';
  var isMobileDevice = checkIfMobileDevice();
  /* 
    The change to add a tabIndex of -1 on the ButtonBase element is because while button primitives 
    natively will render a disabled button non-focusable. The case when using a href this element is an atag. For this, we need to restrict it's focus.
  */

  var ButtonBase = href ? StyledAnchor : StyledButton;
  return /*#__PURE__*/React.createElement(ButtonBase, _extends({}, props, {
    href: href,
    size: size,
    width: width,
    "aria-label": newAriaLabel,
    role: "button",
    "aria-disabled": props.disabled,
    tabIndex: disabled ? -1 : tabIndex ? tabIndex : 0,
    display: display,
    disabled: disabled,
    calculateButtonHeight: calculateButtonHeight,
    calculateBorderRadius: calculateBorderRadius,
    calculateSideMargin: calculateSideMargin,
    "data-loc": dataLoc,
    secondary: use === 'secondary',
    isMobileDevice: isMobileDevice
  }), !disabled && /*#__PURE__*/React.createElement(HitArea, null), /*#__PURE__*/React.createElement(StyledChildWrapper, {
    tabIndex: -1,
    display: display,
    size: size,
    width: width,
    calculateSideMargin: calculateSideMargin,
    calculateBorderRadius: calculateBorderRadius,
    smallButtonPadding: smallButtonPadding
  }, children));
};

Button.defaultProps = defaultProps;
Button.propTypes = propTypes;
Button.displayName = 'Button'; // Set this so ModalFooter can recognize this

export default withVDSManager(Button);